import { GridColDef } from '@mui/x-data-grid-pro';
import React, { useCallback, useContext, useMemo, useState } from 'react';
import EDCheckbox from '../../../../../../../../../BaseComponents/Checkboxes/EDCheckbox';
import EDBox from '../../../../../../../../../BaseComponents/EDBox/EDBox';
import EDAvenyProgress from '../../../../../../../../../BaseComponents/ProgressIndicators/AvenyProgress/EDAvenyProgress';
import EDDataGrid, {
  RowComponentType,
} from '../../../../../../../../../BaseComponents/XGrid/EDDataGrid';
import { KodplanContext } from '../../../../../../../../../Components/KodplanManager/Context/KodplanContext';
import useNodeQuery from '../../../../../../../../../Components/QueryComponents/NodeQuery/useNodeQuery';
import { findFormatter } from '../../../../../../../../../Utils/Formatters/FindFormatter';
import KonteringContext from '../../../../../../../_Common/Components/KonteringManager/KonteringContext';
import { isVisibleByKontodelDate } from '../../../../../../../_Common/Components/KonteringManager/validateKonteringForAttestUtils';
import query from '../../Queries/BokforderVerificationQuery.gql';
import {
  BokforderVerificationQuery,
  BokforderVerificationQuery_node_TEDEKBokfver,
  BokforderVerificationQueryVariables,
  BokforderVerificationQuery_node_TEDEKBokfver__Associations_Konteringar_All,
} from '../../Queries/_QueryTypes/BokforderVerificationQuery';
import { GridRow } from '@mui/x-data-grid-pro';
import EDTooltip from '../../../../../../../../../BaseComponents/Tooltip/EDTooltip';
import BokforderContext from '../../../BokforderContext/BokforderContext';
import {
  createKodstrangDelarFromRow,
  createKodstrangFromRow,
  filterUsersOnKodstrang,
} from '../../../../Utils/BokforderUtils';
import { ISelectedVerRow } from '../../BokforderCreateDialog';
import {
  convertKonteringsRowsToVerRow,
  IVerRow,
  YesOrNoEnum,
} from '../../Utils/BokforderCreateDialogUtils';
import { ActiveDatabaseContext } from '../../../../../../../../../Components/ActiveDatabaseContext/ActiveDatabaseContext';
import { IKodstrangDel } from '../../../../../../../_Common/Components/KonteringManager/KonteringManager';
import { IUserBokforderKodbehorigheter } from '../../../BokforderContext/BokforderContextManager';
import BokforderPeriodDateHandler, {
  IDateInputValue,
} from '../../../BokforderPeriodDialog/Components/BokforderPeriodDateHandler/BokforderPeriodDateHandler';
import { onDateBlur } from '../../../BokforderPeriodDialog/Utils/BokforderPeriodDialogUtils';
import {
  TEDEKBokforderVariantEnum,
  TEDEKKontodelREDOVISN_TYPEnum,
  TEDInputSelectionCondition,
  TEDSelectionConditionType,
  TEDSelectionOperator,
} from '../../../../../../../../../globalQueryTypes';
import EDTypography from '../../../../../../../../../BaseComponents/Typography/EDTypography';
import { BusinessDataFormContext } from '../../../../../../../_Common/Components/BusinessCollectionComponents/BusinessDataForm/BusinessDataForm';
import { BokforderType__Associations_RefBokfver__Associations_Handelser_All } from '../../../../Queries/_QueryTypes/BokforderType';

export interface IVerRowWithPeriod extends IVerRow {
  periodFrom?: string;
  periodTo?: string;
}

interface IProps {
  chosenRows: IVerRow[];
  includeRowText: boolean;
  periodChosenRows: IVerRowWithPeriod[];
  selectedVerRow: ISelectedVerRow | undefined;
  setChosenRows: React.Dispatch<React.SetStateAction<IVerRow[]>>;
  setIncludeRowText: React.Dispatch<React.SetStateAction<boolean>>;
  setPeriodChosenRows: React.Dispatch<
    React.SetStateAction<IVerRowWithPeriod[]>
  >;
  setVerification: React.Dispatch<
    React.SetStateAction<BokforderVerificationQuery_node_TEDEKBokfver | null>
  >;
}

interface IRowComponentWithTooltip {
  row: IVerRow;
  availableRows: BokforderVerificationQuery_node_TEDEKBokfver__Associations_Konteringar_All[];
  bokforderType: TEDEKBokforderVariantEnum;
  getRowDisabledReason: (row: IVerRow) => string[];
}

export const RowComponentWithTooltip: RowComponentType<
  IRowComponentWithTooltip
> = props => {
  const disabledReason = props
    .getRowDisabledReason(props.row)
    .map((error, index) => {
      return <EDBox key={index}>{error}</EDBox>;
    });

  const disabled = disabledReason.length > 0;

  return (
    <>
      {disabled ? (
        <EDTooltip title={disabledReason} enableClickOnTouch>
          <GridRow
            {...props}
            style={{
              // eslint-disable-next-line aveny/no-color-rule
              color: 'rgba(0,0,0,0.38)',
            }}
          />
        </EDTooltip>
      ) : (
        <GridRow {...props} style={{ cursor: 'pointer' }} />
      )}
    </>
  );
};

const TODAY = new Date();

const currFormatter = findFormatter('TEDFormatCurr');

const BokforderVerificationWizardStep2: React.FC<
  React.PropsWithChildren<IProps>
> = props => {
  const { setChosenRows, setPeriodChosenRows } = props;
  const bokforderContext = useContext(BokforderContext);
  const formContext = useContext(BusinessDataFormContext);
  const kodplanContext = useContext(KodplanContext);
  const konteringContext = useContext(KonteringContext);
  const getKontoPlanById = konteringContext.konteringUtils.getKontoPlanById;
  const { userID } = useContext(ActiveDatabaseContext);
  const [fromDateInputValue, setFromDateInputValue] = useState<IDateInputValue>(
    { value: '', isValid: false }
  );
  const [toDateInputValue, setToDateInputValue] = useState<IDateInputValue>({
    value: '',
    isValid: false,
  });
  const [infoSummeringDialogOpen, setInfoSummeringDialogOpen] =
    useState<boolean>(false);

  const [
    infoDiffMoreThanOneYearDialogOpen,
    setInfoDiffMoreThanOneYearDialogOpen,
  ] = useState<boolean>(false);

  const kodplanID = useMemo(
    () => kodplanContext.activeKodplan?.id ?? '',
    [kodplanContext.activeKodplan?.id]
  );

  const bokforderKodplan = useMemo(() => {
    return getKontoPlanById(bokforderContext.bokforderKodplan?.id ?? '');
  }, [bokforderContext.bokforderKodplan?.id, getKontoPlanById]);

  const getRowDisabledReason = useCallback(
    (row: IVerRow): string[] => {
      const errorMessages = [];
      if (!row) {
        errorMessages.push('Informationen för raden kunde inte hittas');
        return errorMessages;
      }

      if (konteringContext.konteringValidationQueryLoading) {
        errorMessages.push('Kodplan hämtas');
      }

      const kontodelDefs =
        konteringContext.konteringUtils.getKontodelDefsByKodplanId(kodplanID);

      const kontodelarInKodstrang: IKodstrangDel[] =
        createKodstrangDelarFromRow(row._Associations, kontodelDefs);

      const kodstrang = createKodstrangFromRow(row, kontodelarInKodstrang);

      const baskonto = kontodelDefs
        .find(def => def.ordningsNr === 1)
        ?.kontodelar.find(
          kontodel => kontodel.id === row._Associations?.Kontodel1?.id
        );

      if (
        baskonto?.redovisningsTyp ===
          TEDEKKontodelREDOVISN_TYPEnum.STALLNINGSKONTO &&
        bokforderContext.bokforderVariant ===
          TEDEKBokforderVariantEnum.PERIODISERA
      ) {
        errorMessages.push('Balanskonton kan inte periodiseras');
      }

      if (row.Periodiserad === YesOrNoEnum.Yes) {
        errorMessages.push(
          'Raden är periodiserad och kan då inte hanteras i Bokföringsorder'
        );
      }

      // Kolla efter kombinationsvillkorfel
      if (bokforderKodplan) {
        const kombinationVillkorErrors =
          konteringContext.konteringUtils.getKombinationsVillkorsErrors(
            kodstrang,
            bokforderKodplan,
            bokforderContext.bokforderDate
          );

        if (kombinationVillkorErrors.length > 0) {
          errorMessages.push(kombinationVillkorErrors[0].meddelande);
        }

        const kombinationErrors =
          konteringContext.konteringUtils.getKombinationErrors(
            kodstrang,
            bokforderKodplan,
            bokforderContext.bokforderDate
          );

        if (kombinationErrors.length > 0) {
          errorMessages.push(kombinationErrors[0].meddelande);
        }
      }
      for (const [key, value] of Object.entries(row?._Associations ?? {})) {
        if (key.includes('Kontodel')) {
          const kontodel =
            konteringContext.konteringUtils.getKontodelByKontodel(
              kodplanID,
              value?.ORDNINGS_NR ?? -1,
              value?.KONTODEL ?? '',
              TODAY
            );

          if (kontodel) {
            if (!isVisibleByKontodelDate(kontodel, TODAY) || !kontodel.aktiv) {
              errorMessages.push('Raden innehåller koddelar som är inaktiva');
            } else if (kontodel.sparraManuell) {
              errorMessages.push('Baskontot är spärrat för manuell bokföring');
            }
          } else if (kontodel == null && value?.KONTODEL != null) {
            errorMessages.push('Raden innehåller koddelar som är inaktiva');
          }
        }
      }

      if (
        bokforderContext.bokforderVariant ===
          TEDEKBokforderVariantEnum.OMBOKFOR ||
        bokforderContext.bokforderVariant === TEDEKBokforderVariantEnum.VAND ||
        bokforderContext.bokforderVariant ===
          TEDEKBokforderVariantEnum.PERIODISERA
      ) {
        if (row.Automatkonterat === YesOrNoEnum.Yes) {
          errorMessages.push('Automatkonterade rader går ej att välja');
        }

        if (bokforderContext.limitedCreateMode) {
          const currentUser = bokforderContext.allBokforderUsersWithKodbeh.find(
            user => user.userID === userID
          );

          if (!currentUser || !kodplanID) {
            errorMessages.push('Aktuell användare kunde inte hittas');
          }
          const bokfKodbeh = currentUser?.bokforderKodbehorighetUrval;

          if (!bokfKodbeh) {
            errorMessages.push('Användarens kodbehörighet kunde inte hittas');
          }

          let usersWithBfoKodbeh: IUserBokforderKodbehorigheter[] =
            bokforderContext.allBokforderUsersWithKodbeh;
          if (kontodelarInKodstrang.length > 0) {
            usersWithBfoKodbeh =
              bokforderContext.allBokforderUsersWithKodbeh.filter(
                filterUsersOnKodstrang(kontodelarInKodstrang)
              );
          }
          const currentUserIsOkey =
            usersWithBfoKodbeh.find(
              user => user.userID === currentUser?.userID
            ) != undefined;

          if (!currentUserIsOkey) {
            errorMessages.push(
              'Raden innehåller koddelar utanför dina kodbehörigheter'
            );
          }
        }
      }

      return errorMessages;
    },
    [
      bokforderContext.allBokforderUsersWithKodbeh,
      bokforderContext.bokforderDate,
      bokforderContext.bokforderVariant,
      bokforderContext.limitedCreateMode,
      bokforderKodplan,
      kodplanID,
      konteringContext.konteringUtils,
      konteringContext.konteringValidationQueryLoading,
      userID,
    ]
  );

  const filterRows = useCallback(
    (row: IVerRow) => {
      const disabledReason = getRowDisabledReason(row);
      return disabledReason.length == 0;
    },
    [getRowDisabledReason]
  );

  const customSelection: TEDInputSelectionCondition[][] = useMemo(() => {
    return [
      [
        {
          namn: 'FiltreraEnligtMinKodbehorighetDatum',
          ass: [],
          typ: TEDSelectionConditionType.Value,
          valueCondition: {
            op: TEDSelectionOperator.equals,
            value: [
              {
                value: formContext?.formState.currentValues?.BOKF_DATUM
                  ? formContext?.formState.currentValues?.BOKF_DATUM
                  : new Date().toLocaleDateString('sv-SE'),
              },
            ],
          },
        },
      ],
    ];
  }, [formContext?.formState.currentValues?.BOKF_DATUM]);

  const { nodeData, loading } = useNodeQuery<
    BokforderVerificationQuery,
    BokforderVerificationQuery_node_TEDEKBokfver,
    BokforderVerificationQueryVariables
  >(query, props?.selectedVerRow?.id ?? '', {
    skip:
      props.selectedVerRow &&
      (props.selectedVerRow.id == null || props.selectedVerRow.id === ''),
    onCompleted: data => {
      const konteringRows =
        (data?.node as BokforderVerificationQuery_node_TEDEKBokfver)
          ?._Associations?.Konteringar_All ?? [];

      const rows = convertKonteringsRowsToVerRow(konteringRows, currFormatter);
      const filteredRows = rows.filter(filterRows);

      const bokfVerData =
        data?.node as BokforderVerificationQuery_node_TEDEKBokfver;
      props.setChosenRows(filteredRows);
      props.setVerification(bokfVerData);

      const hasUnderlag =
        (
          bokfVerData?._Associations?.Bokforingsorder?._Associations
            ?.Dokument_All ?? []
        ).length > 0;

      if (hasUnderlag) {
        bokforderContext.setHasVerifikationUnderlag(true);
      }

      const hasFakturaBild =
        (
          bokfVerData?._Associations?.Handelser_All?.map(
            (
              handelse: BokforderType__Associations_RefBokfver__Associations_Handelser_All
            ) => {
              return handelse?._Associations?.Levfaktura?._Associations
                ?.Fakturasidor_All;
            }
          ) ?? []
        ).length > 0;

      if (hasFakturaBild) {
        bokforderContext.setHasVerifikationFakturabild(true);
      }

      formContext?.update([
        [{ operation: 'OwnFields', values: { BOKF_DATUM: bokfVerData.DATUM } }],
      ]);
    },
    variables: {
      id: props?.selectedVerRow?.id ?? '',
      customSelection: bokforderContext.limitedCreateMode
        ? customSelection
        : [],
    },
  });
  const rows = useMemo(() => {
    if (nodeData?._Associations?.Konteringar_All) {
      const verRows = convertKonteringsRowsToVerRow(
        nodeData?._Associations?.Konteringar_All,
        currFormatter
      );

      if (
        bokforderContext.bokforderVariant ===
        TEDEKBokforderVariantEnum.PERIODISERA
      ) {
        const convertedRowsWithPeriod: IVerRowWithPeriod[] = verRows.map(
          row => {
            const periodDataForRow = props.periodChosenRows.find(
              periodRow => periodRow.id === row.id
            );

            return {
              ...row,
              periodFrom: periodDataForRow?.periodFrom,
              periodTo: periodDataForRow?.periodTo,
            };
          }
        );

        return convertedRowsWithPeriod;
      }

      return verRows;
    }
    return [];
  }, [
    bokforderContext.bokforderVariant,
    nodeData?._Associations?.Konteringar_All,
    props.periodChosenRows,
  ]);

  const filteredRows: IVerRow[] = useMemo(
    () => rows.filter(filterRows),
    [filterRows, rows]
  );

  const showPeriodColumn = useMemo(
    () =>
      rows.some(row => row.Periodiserad === YesOrNoEnum.Yes) ||
      bokforderContext.bokforderVariant ===
        TEDEKBokforderVariantEnum.PERIODISERA,
    [bokforderContext.bokforderVariant, rows]
  );
  const showAutoColumn = useMemo(
    () => rows.some(row => row.Automatkonterat === YesOrNoEnum.Yes),
    [rows]
  );

  const onCheckboxClick = useCallback(
    (rowID: string) => (isChecked: boolean) => {
      if (isChecked) {
        const addedRow = rows.find(row => row.id === rowID.toString());

        if (addedRow) {
          setChosenRows([...props.chosenRows, addedRow]);
        }
      } else {
        const newRows = props.chosenRows.filter(
          row => row.id !== rowID.toString()
        );
        setChosenRows([...newRows]);
      }
    },
    [props.chosenRows, setChosenRows, rows]
  );

  const columns: GridColDef<any>[] = useMemo(() => {
    const tempColumns: GridColDef<any>[] = [
      {
        align: 'center',
        field: 'Vald',
        renderCell: params => {
          const disabled = !filteredRows.some(
            row => row.id === params.id.toString()
          );

          return (
            <EDCheckbox
              checked={props.chosenRows.some(
                row => row.id === params.id.toString()
              )}
              disabled={disabled}
              onCheck={onCheckboxClick(params.id.toString())}
            />
          );
        },
        minWidth: 46,
      },
      {
        field: 'Kontostrang',
        headerName: 'Kodsträng',
        align: undefined,
        minWidth: 200,
      },
      {
        field: 'Kontoben',
        headerName: 'Benämning',
        align: undefined,
        minWidth: 460,
      },
      { field: 'Debet', align: 'right', headerAlign: 'right' },
      { field: 'Kredit', align: 'right', headerAlign: 'right' },
    ];

    if (
      bokforderContext.bokforderVariant === TEDEKBokforderVariantEnum.KOPIERA
    ) {
      tempColumns.push({ field: 'Text', minWidth: 100 });
    }

    if (showPeriodColumn) {
      tempColumns.push(
        {
          field: 'periodFrom',
          headerName: 'Period, From',
          width: 120,
        },
        {
          field: 'periodTo',
          headerName: 'Period, Tom',
          width: 120,
        }
      );
    }

    if (showAutoColumn) {
      tempColumns.push({ field: 'Automatkonterat', align: undefined });
    }

    return tempColumns;
  }, [
    bokforderContext.bokforderVariant,
    filteredRows,
    onCheckboxClick,
    props.chosenRows,
    showAutoColumn,
    showPeriodColumn,
  ]);

  const onPeriodUseClick = useCallback(() => {
    const newPeriodRows: IVerRowWithPeriod[] = [];
    const newPeriodIDs: string[] = [];

    const newChosenRows = props.chosenRows.map(chosenRow => {
      const rowPeriodData: IVerRowWithPeriod = {
        ...chosenRow,
        periodFrom: fromDateInputValue.value ?? '',
        periodTo: toDateInputValue.value ?? '',
      };

      newPeriodRows.push(rowPeriodData);
      newPeriodIDs.push(chosenRow.id);

      return {
        ...chosenRow,
        ...rowPeriodData,
      };
    });

    const newPeriodData = props.periodChosenRows
      .filter(periodRow => !newPeriodIDs.includes(periodRow.id))
      .concat(newPeriodRows);

    setPeriodChosenRows(newPeriodData);

    setChosenRows(newChosenRows);
  }, [
    fromDateInputValue,
    props.chosenRows,
    props.periodChosenRows,
    setChosenRows,
    setPeriodChosenRows,
    toDateInputValue,
  ]);

  return (
    <EDBox sx={{ height: '100%' }}>
      {loading && <EDAvenyProgress />}
      <EDBox
        sx={{
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'center',
          justifyContent: 'flex-start',
        }}
      >
        <EDTypography
          sx={{
            padding: '0px 16px 0px 0px',
          }}
        >
          <EDTypography bold component="span">
            {'Verifikationsnummer: '}
          </EDTypography>
          {nodeData?.VER_NR}
        </EDTypography>
        <EDTypography
          sx={{
            padding: '0px 16px 0px 0px',
          }}
        >
          <EDTypography bold component="span">
            {'Datum: '}
          </EDTypography>
          {nodeData?.DATUM}
        </EDTypography>
      </EDBox>
      {bokforderContext.bokforderVariant ===
      TEDEKBokforderVariantEnum.PERIODISERA ? (
        <BokforderPeriodDateHandler<IVerRow>
          availableRows={filteredRows}
          chosenRows={props.chosenRows}
          fromDateInputValue={fromDateInputValue}
          infoDiffMoreThanOneYearDialogOpen={infoDiffMoreThanOneYearDialogOpen}
          infoSummeringDialogOpen={infoSummeringDialogOpen}
          onClearClick={() => {
            const newPeriodData = props.periodChosenRows.filter(
              periodRow =>
                !props.chosenRows.some(
                  chosenRow => chosenRow.id === periodRow.id
                )
            );

            props.setPeriodChosenRows(newPeriodData);
          }}
          onDateBlur={onDateBlur}
          onUseClick={onPeriodUseClick}
          setChosenRows={props.setChosenRows}
          setFromDateInputValue={setFromDateInputValue}
          setInfoDiffMoreThanOneYearDialogOpen={
            setInfoDiffMoreThanOneYearDialogOpen
          }
          setInfoSummeringDialogOpen={setInfoSummeringDialogOpen}
          setToDateInputValue={setToDateInputValue}
          toDateInputValue={toDateInputValue}
        />
      ) : (
        <>
          <EDCheckbox
            indeterminate={
              props.chosenRows.length > 0
                ? props.chosenRows.length === filteredRows.length
                  ? false
                  : true
                : false
            }
            checked={filteredRows.length === props.chosenRows.length}
            onCheck={isInputChecked => {
              if (isInputChecked) {
                props.setChosenRows(filteredRows);
              } else {
                props.setChosenRows([]);
              }
            }}
            label={'Välj alla'}
          />
          <EDCheckbox
            checked={props.includeRowText}
            onCheck={isInputChecked => {
              props.setIncludeRowText(isInputChecked);
            }}
            label={'Inkludera radtexter'}
          />
        </>
      )}

      <EDBox sx={{ height: 'calc(100% - 74px)' }}>
        <EDDataGrid
          columns={columns}
          disableColumnMenu
          rows={rows ?? []}
          onRowClick={(params, event) => {
            const canBeClicked = filteredRows.some(
              row => row.id === params.id.toString()
            );

            if (canBeClicked) {
              const isChecked = props.chosenRows.some(
                chosenRow => chosenRow.id === params.id.toString()
              );

              onCheckboxClick(params.id.toString())(!isChecked);
            }
          }}
          RowComponent={RowComponentWithTooltip}
          rowProps={{
            filteredRows: filteredRows,
            bokforderType: bokforderContext.bokforderVariant,
            getRowDisabledReason: getRowDisabledReason,
          }}
          testId="verfikationsrader-grid"
        />
      </EDBox>
    </EDBox>
  );
};

export default BokforderVerificationWizardStep2;
